import { skipToken, useQuery } from '@tanstack/react-query'
import { type ReactElement, use } from 'react'

import { useTranslate } from '@orus.eu/pharaoh'
import { Dialog } from '@orus.eu/pharaoh-next/components/patterns/dialog'

import { BrokerContactSections } from './components/broker-contact-sections.tsx'
import { OrusContactChannels } from './components/orus-contact-channels.tsx'

import { useTRPC } from '#/client.ts'
import { useEmbeddingPartnerConfiguration } from '#/lib/embedding-partner.ts'
import { helpContactDetails } from '#/lib/help-contact-details.ts'
import { useOperatingZone } from '#/lib/operating-zone-context.ts'
import { SessionContext } from '#/lib/session.ts'

type HelpContactDialogProps = {
  open: boolean
  onClose: () => void
}

export function HelpContactDialog({ open, onClose }: HelpContactDialogProps): ReactElement {
  const translate = useTranslate()
  const operatingZone = useOperatingZone()
  const embeddingPartnerConfiguration = useEmbeddingPartnerConfiguration()
  const trpc = useTRPC()
  const session = use(SessionContext)

  const isConnectedAsCustomer = !!session?.user && session?.permissions?.type === 'client'
  const shouldResolveBroker = open && isConnectedAsCustomer && !embeddingPartnerConfiguration

  // null when there is no broker, or the context is ambiguous (Orus + broker, or multiple brokers)
  const { data: brokerHelpContactInformation } = useQuery(
    trpc.contracts.getHelpContactInformation.queryOptions(shouldResolveBroker ? undefined : skipToken),
  )

  const { email, whatsappLink, phone } =
    embeddingPartnerConfiguration?.helpContactDetails ?? helpContactDetails[operatingZone]

  const title = brokerHelpContactInformation
    ? translate('chat_fallback_managed_by_broker_title')
    : (embeddingPartnerConfiguration?.helpDialogTitle ?? translate('chat_fallback_title'))

  return (
    <Dialog open={open} onClose={onClose} size="medium">
      <Dialog.Header>
        <Dialog.Title>{title}</Dialog.Title>
        <Dialog.CloseIconButton aria-label={translate('close')} />
      </Dialog.Header>

      <Dialog.Body>
        {brokerHelpContactInformation ? (
          <BrokerContactSections
            brokerDisplayName={brokerHelpContactInformation.displayName}
            brokerPhone={brokerHelpContactInformation.phone}
            brokerEmail={brokerHelpContactInformation.email}
            orusEmail={email}
          />
        ) : (
          <>
            <Dialog.Description>
              {embeddingPartnerConfiguration?.helpDialogExplanation ?? translate('chat_fallback_explanation')}
            </Dialog.Description>

            <OrusContactChannels
              email={email}
              whatsappLink={whatsappLink}
              phone={embeddingPartnerConfiguration?.helpContactDetails ? phone : undefined}
            />
          </>
        )}
      </Dialog.Body>
    </Dialog>
  )
}
